"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/app/components/Input"
import { Label } from "@/app/components/Label"
import { Loader2, Sparkles, CheckCircle } from "lucide-react"

interface BusinessWaitlistModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  isLoggedIn?: boolean
}

export function BusinessWaitlistModal({ isOpen, onClose, onSuccess, isLoggedIn = false }: BusinessWaitlistModalProps) {
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/support/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: "Business Waitlist Request",
          email: email,
          subject: "Business Waitlist Request",
          category: "subscriptions",
          message: "Business Waitlist Request",
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to submit waitlist request")
      }

      setIsSubmitting(false)
      setIsSubmitted(true)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error("Error submitting waitlist request:", error)
      setIsSubmitting(false)
      // You could add error state handling here if needed
      alert("Failed to submit waitlist request. Please try again.")
    }
  }

  const handleClose = () => {
    setEmail("")
    setIsSubmitted(false)
    onClose()
  }

  if (isSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 bg-[#40A1AB] rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">Welcome to the Elite Circle! ✨</DialogTitle>
            <DialogDescription asChild>
              <div className="text-center space-y-3">
                <p>
                  {isLoggedIn
                    ? "Perfect! You've been added to our exclusive Business tier waitlist."
                    : "Congratulations! You've secured your spot in our exclusive Business tier waitlist."}
                </p>
                <p className="font-medium text-[#40A1AB]">
                  You're now part of a select group of design professionals who will get priority access to our most
                  powerful features.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="font-semibold text-gray-900">What happens next:</p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Personal invitation when Business tier launches</li>
                    <li>• Exclusive founding member pricing (up to 30% off)</li>
                    <li>• Direct access to our product team for feedback</li>
                    <li>• Priority onboarding and dedicated support</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  {isLoggedIn
                    ? "We'll contact you directly through your account when your invitation is ready."
                    : "We'll be in touch soon with your exclusive invitation. Keep an eye on your inbox!"}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button onClick={handleClose} className="bg-[#40A1AB] hover:bg-[#3A919A] text-white px-8">
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="h-16 w-16 bg-gradient-to-br from-[#40A1AB] to-[#3A919A] rounded-full flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">
            {isLoggedIn ? "Upgrade to Business Tier" : "Join the Business Waitlist"}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-center space-y-3">
              <p className="text-lg font-medium text-[#40A1AB]">Invite-only for select design professionals</p>
              <p>
                Our Business tier is currently in private beta, available exclusively to a curated group of interior
                designers and real estate professionals.
              </p>
              <div className="bg-gradient-to-r from-[#40A1AB]/10 to-[#3A919A]/10 p-4 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Business Tier Benefits:</p>
                <ul className="text-sm text-gray-700 space-y-1 text-left">
                  <li>• Unlimited design credits</li>
                  <li>• Priority rendering (2x faster)</li>
                  <li>• Advanced client management tools</li>
                  <li>• White-label reports and presentations</li>
                  <li>• API access for integrations</li>
                  <li>• Dedicated priority support</li>
                </ul>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLoggedIn && (
            <div className="space-y-2">
              <Label htmlFor="waitlist-email">Professional Email Address</Label>
              <Input
                id="waitlist-email"
                type="email"
                placeholder="your@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>
          )}

          {isLoggedIn && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Ready to join the waitlist?</p>
              <p className="text-xs text-blue-700">
                We'll use your existing account information and contact you directly when Business tier becomes
                available.
              </p>
            </div>
          )}

          <div className="flex flex-col space-y-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || (!isLoggedIn && !email)}
              className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLoggedIn ? "Adding you to waitlist..." : "Securing your spot..."}
                </>
              ) : isLoggedIn ? (
                "Join Business Waitlist"
              ) : (
                "Secure My Exclusive Access"
              )}
            </Button>

            <Button type="button" variant="ghost" onClick={handleClose} className="w-full">
              Maybe Later
            </Button>
          </div>
        </form>

        <div className="text-xs text-center text-gray-500 pt-2">
          Limited spots available. Priority access guaranteed for waitlist members.
        </div>
      </DialogContent>
    </Dialog>
  )
}
